/**
 * Plan badge shown in the task pane header.
 *
 * Renders the current user's plan tier as a small chip (e.g. "Starter")
 * with an "Upgrade" link that opens a pre-filled email to support
 * requesting a tier change. Hidden entirely if the user has no plan
 * (un-provisioned users see the PlansWelcome screen instead, never this).
 */
import React from 'react';
import { PlanTier, PLAN_LABEL } from '../services/authProfile';

interface Props {
  plan: PlanTier;
  contactEmail: string;
}

export function PlanBadge({ plan, contactEmail }: Props): JSX.Element {
  const label = PLAN_LABEL[plan];
  // Enterprise users don't see an Upgrade link — they're already top tier.
  const showUpgrade = plan !== 'tier4';
  const upgradeHref = showUpgrade
    ? `mailto:${contactEmail}?subject=${encodeURIComponent('Practice IQ Reporting — Upgrade request')}&body=${encodeURIComponent(
        `Hi Practice IQ team,\n\nI'm currently on the ${label} plan and would like to upgrade.\n\nThanks!`
      )}`
    : undefined;

  return (
    <div style={s.wrap}>
      <span style={s.tier}>{label}</span>
      {upgradeHref && (
        <>
          <span style={s.sep}>·</span>
          <a href={upgradeHref} style={s.upgradeLink}>Upgrade →</a>
        </>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    lineHeight: 1.2,
    color: '#666',
    marginTop: 1,
    fontWeight: 400,
  },
  tier: {
    background: '#e8eef7',
    color: '#1F3864',
    padding: '1px 6px',
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.3px',
  },
  sep: {
    color: '#bbb',
  },
  upgradeLink: {
    color: '#1F3864',
    textDecoration: 'none',
    fontSize: 10,
    fontWeight: 500,
  },
};
